import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';

const CONTENT_TYPES = [
    { key: 'release', label: 'Álbum / Release' },
    { key: 'playlist', label: 'Playlist' },
    { key: 'artist', label: 'Artista' },
    { key: 'music', label: 'Música' },
];

const ACTIVITY_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'active_7', label: 'Activos nos últimos 7 dias' },
    { value: 'active_30', label: 'Activos nos últimos 30 dias' },
    { value: 'active_90', label: 'Activos nos últimos 90 dias' },
    { value: 'inactive_30', label: 'Inactivos há mais de 30 dias' },
    { value: 'inactive_90', label: 'Inactivos há mais de 90 dias' },
    { value: 'never', label: 'Nunca ouviram nada' },
];

const ACC_TYPE_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'listener', label: 'Ouvintes' },
    { value: 'artist', label: 'Artistas' },
    { value: 'creator', label: 'Creators' },
    { value: 'podcaster', label: 'Podcasters' },
];

const PICKER_LIMIT = 15;

/** Meta line under each result — the reason the item sits where it sits. */
const itemMeta = (item, type) => {
    const parts = [];
    if (item.play_count > 0) {
        parts.push(`${item.play_count} ${item.play_count === 1 ? 'reprodução' : 'reproduções'}`);
    }
    if (type !== 'music' && item.song_count > 0) {
        parts.push(`${item.song_count} ${item.song_count === 1 ? 'música' : 'músicas'}`);
    }
    if (!item.image) parts.push('sem imagem');
    return parts.join(' · ');
};

const PREMIUM_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'premium', label: 'Só Premium' },
    { value: 'free', label: 'Só gratuitos' },
];

export default function PushNotifications() {
    // Composer
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [appUrl, setAppUrl] = useState('');
    const [webUrl, setWebUrl] = useState('');
    const [contentType, setContentType] = useState(null);
    const [contentId, setContentId] = useState(null);

    // Content picker
    const [pickerType, setPickerType] = useState('release');
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerItems, setPickerItems] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerPage, setPickerPage] = useState(1);
    const [pickerPages, setPickerPages] = useState(1);
    const [pickerTotal, setPickerTotal] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);

    // Segmentation
    const [activity, setActivity] = useState('');
    const [accType, setAccType] = useState('');
    const [premium, setPremium] = useState('');
    const [country, setCountry] = useState('');
    const [countries, setCountries] = useState([]);
    const [audienceCount, setAudienceCount] = useState(null);
    const [countingAudience, setCountingAudience] = useState(false);

    // Sending
    const [testTarget, setTestTarget] = useState('');
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null);

    // History
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const filters = { activity, acc_type: accType, premium, country };

    /* ----------------------------- audience ----------------------------- */

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            setCountingAudience(true);
            try {
                const { data } = await izenuAPI.post('/admin/notification/previewAudience.php', { filters });
                if (!cancelled && data.response) {
                    setAudienceCount(data.count);
                    if (data.countries) setCountries(data.countries);
                }
            } catch (e) {
                console.log('previewAudience error', e);
                if (!cancelled) setAudienceCount(null);
            }
            if (!cancelled) setCountingAudience(false);
        }, 400);

        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity, accType, premium, country]);

    /* --------------------------- content picker -------------------------- */

    const fetchContent = useCallback(async () => {
        setPickerLoading(true);
        try {
            const { data } = await izenuAPI.get('/admin/notification/contentSearch.php', {
                params: { type: pickerType, search: pickerSearch, page: pickerPage, limit: PICKER_LIMIT },
            });
            if (data.response) {
                setPickerItems(data.items || []);
                setPickerPages(data.pages || 1);
                setPickerTotal(data.total || 0);
            }
        } catch (e) {
            console.log('contentSearch error', e);
            setPickerItems([]);
            setPickerPages(1);
            setPickerTotal(0);
        }
        setPickerLoading(false);
    }, [pickerType, pickerSearch, pickerPage]);

    useEffect(() => {
        const timer = setTimeout(fetchContent, pickerSearch ? 400 : 0);
        return () => clearTimeout(timer);
    }, [fetchContent, pickerSearch]);

    const selectContent = (item) => {
        setSelectedItem(item);
        setContentType(pickerType);
        setContentId(item.id);
        setAppUrl(item.app_url || '');
        setWebUrl(item.web_url || '');
        setImageUrl(item.image || '');
        if (!title) {
            setTitle(pickerType === 'release' ? 'Novo álbum na Izenu 🎧' : 'Nova playlist na Izenu 🎶');
        }
        if (!message) setMessage(`${item.title} — ${item.subtitle}`);
    };

    const clearContent = () => {
        setSelectedItem(null);
        setContentType(null);
        setContentId(null);
        setAppUrl('');
        setWebUrl('');
        setImageUrl('');
    };

    /* ------------------------------ history ------------------------------ */

    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const { data } = await izenuAPI.get('/admin/notification/history.php', {
                params: { limit: 20, stats: 1 },
            });
            if (data.response) setHistory(data.campaigns || []);
        } catch (e) {
            console.log('history error', e);
        }
        setHistoryLoading(false);
    }, []);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    /* ------------------------------ sending ------------------------------ */

    const basePayload = () => ({
        title,
        message,
        image_url: imageUrl,
        app_url: appUrl,
        web_url: webUrl,
        content_type: contentType,
        content_id: contentId,
        filters,
    });

    const validate = () => {
        if (!title.trim() || !message.trim()) {
            setFeedback({ type: 'error', text: 'Título e mensagem são obrigatórios.' });
            return false;
        }
        return true;
    };

    const sendTest = async () => {
        if (!validate()) return;
        if (!testTarget.trim()) {
            setFeedback({ type: 'error', text: 'Indica o email ou user_id da conta da app para o teste.' });
            return;
        }

        setSending(true);
        setFeedback(null);
        try {
            const { data } = await izenuAPI.post('/admin/notification/send.php', {
                ...basePayload(),
                is_test: true,
                test_target: testTarget.trim(),
            });
            setFeedback({ type: data.response ? 'ok' : 'error', text: data.message });
            fetchHistory();
        } catch (e) {
            setFeedback({ type: 'error', text: e.response?.data?.message || 'Erro ao enviar o teste.' });
        }
        setSending(false);
    };

    const sendBroadcast = async () => {
        if (!validate()) return;
        if (audienceCount === null || audienceCount === 0) {
            setFeedback({ type: 'error', text: 'O segmento seleccionado não tem destinatários.' });
            return;
        }

        const ok = window.confirm(
            `Vais enviar esta notificação a ${audienceCount} utilizador(es).\n\n` +
            `Título: ${title}\nMensagem: ${message}\n\nEsta acção não pode ser revertida. Continuar?`
        );
        if (!ok) return;

        setSending(true);
        setFeedback(null);
        try {
            const { data } = await izenuAPI.post('/admin/notification/send.php', {
                ...basePayload(),
                is_test: false,
                confirm: true,
            });
            setFeedback({ type: data.response ? 'ok' : 'error', text: data.message });
            fetchHistory();
        } catch (e) {
            setFeedback({ type: 'error', text: e.response?.data?.message || 'Erro ao enviar.' });
        }
        setSending(false);
    };

    /* ------------------------------- render ------------------------------ */

    return (
        <div style={s.container}>
            <div style={s.header}>
                <h1 style={s.title}>Notificações Push</h1>
                <p style={s.subtitle}>
                    Envia novidades para os utilizadores da app e da web. Divulga playlists, álbuns à venda
                    e incentiva quem anda afastado a voltar.
                </p>
            </div>

            <div style={s.columns}>
                {/* ------------------------- left column ------------------------- */}
                <div style={s.col}>
                    <div style={s.card}>
                        <h3 style={s.cardTitle}>1. Conteúdo a divulgar</h3>
                        <p style={s.cardDesc}>
                            Opcional. Ao escolher um item, o link e a imagem são preenchidos automaticamente.
                        </p>

                        <div style={s.chipRow}>
                            {CONTENT_TYPES.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => { setPickerType(t.key); setPickerItems([]); setPickerPage(1); }}
                                    style={pickerType === t.key ? s.chipActive : s.chip}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <input
                            type="text"
                            placeholder="Pesquisar..."
                            value={pickerSearch}
                            onChange={(e) => { setPickerSearch(e.target.value); setPickerPage(1); }}
                            style={{ ...s.input, width: '100%', marginBottom: 10 }}
                        />

                        {selectedItem && (
                            <div style={s.selectedBox}>
                                {selectedItem.image
                                    ? <img src={selectedItem.image} alt="" style={s.thumb} />
                                    : <div style={s.thumbPlaceholder} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={s.itemTitle}>{selectedItem.title}</div>
                                    <div style={s.itemSub}>{selectedItem.subtitle}</div>
                                </div>
                                <button onClick={clearContent} style={s.removeBtn}>Remover</button>
                            </div>
                        )}

                        <div style={s.pickerList}>
                            {pickerLoading ? (
                                <p style={s.empty}>A carregar...</p>
                            ) : pickerItems.length === 0 ? (
                                <p style={s.empty}>Sem resultados.</p>
                            ) : (
                                pickerItems.map((item) => (
                                    <div
                                        key={`${pickerType}-${item.id}`}
                                        onClick={() => selectContent(item)}
                                        style={s.pickerRow}
                                    >
                                        {item.image
                                            ? <img src={item.image} alt="" style={s.thumb} />
                                            : <div style={s.thumbPlaceholder} />}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={s.itemTitle}>{item.title}</div>
                                            <div style={s.itemSub}>{item.subtitle}</div>
                                            {itemMeta(item, pickerType) && (
                                                <div style={s.itemMeta}>{itemMeta(item, pickerType)}</div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {pickerTotal > 0 && (
                            <div style={s.pager}>
                                <button
                                    onClick={() => setPickerPage((p) => Math.max(1, p - 1))}
                                    disabled={pickerPage <= 1 || pickerLoading}
                                    style={pickerPage <= 1 || pickerLoading ? s.pagerBtnDisabled : s.pagerBtn}
                                >
                                    ‹ Anterior
                                </button>
                                <span style={s.pagerLabel}>
                                    Página {pickerPage} de {pickerPages} · {pickerTotal} {pickerTotal === 1 ? 'item' : 'itens'}
                                </span>
                                <button
                                    onClick={() => setPickerPage((p) => Math.min(pickerPages, p + 1))}
                                    disabled={pickerPage >= pickerPages || pickerLoading}
                                    style={pickerPage >= pickerPages || pickerLoading ? s.pagerBtnDisabled : s.pagerBtn}
                                >
                                    Seguinte ›
                                </button>
                            </div>
                        )}

                        <p style={s.pickerNote}>
                            Ordenado por: com imagem primeiro, depois mais reproduzidos e com mais músicas.
                        </p>
                    </div>

                    <div style={{ ...s.card, marginTop: 16 }}>
                        <h3 style={s.cardTitle}>2. Mensagem</h3>

                        <label style={s.fieldLabel}>Título</label>
                        <input
                            type="text"
                            value={title}
                            maxLength={65}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Novo álbum na Izenu 🎧"
                            style={{ ...s.input, width: '100%', marginBottom: 4 }}
                        />
                        <div style={s.counter}>{title.length}/65</div>

                        <label style={s.fieldLabel}>Mensagem</label>
                        <textarea
                            value={message}
                            maxLength={180}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Já podes ouvir o novo álbum de..."
                            style={{ ...s.input, width: '100%', minHeight: 70, resize: 'vertical', marginBottom: 4 }}
                        />
                        <div style={s.counter}>{message.length}/180</div>

                        <label style={s.fieldLabel}>Imagem (URL)</label>
                        <input
                            type="text"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https://dpagz47n4g1lw.cloudfront.net/..."
                            style={{ ...s.input, width: '100%', marginBottom: 12 }}
                        />

                        <label style={s.fieldLabel}>Link na app (deeplink)</label>
                        <input
                            type="text"
                            value={appUrl}
                            onChange={(e) => setAppUrl(e.target.value)}
                            placeholder="izenu://release/?id=812"
                            style={{ ...s.input, width: '100%', marginBottom: 12 }}
                        />

                        <label style={s.fieldLabel}>Link na web</label>
                        <input
                            type="text"
                            value={webUrl}
                            onChange={(e) => setWebUrl(e.target.value)}
                            placeholder="https://app.izenu.ao/release/812"
                            style={{ ...s.input, width: '100%' }}
                        />

                        <div style={s.helpBox}>
                            <strong style={{ color: '#555' }}>Deeplinks aceites na app:</strong>
                            <div style={s.helpGrid}>
                                <code style={s.code}>izenu://release/?id=X</code>
                                <code style={s.code}>izenu://playlist/?id=X</code>
                                <code style={s.code}>izenu://song/?song_id=X</code>
                                <code style={s.code}>izenu://artist/?id=X</code>
                                <code style={s.code}>izenu://play/?id=X</code>
                                <code style={s.code}>izenu://podcast/</code>
                            </div>
                            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#999' }}>
                                Se o conteúdo não existir, a app abre a home. Os links de playlist e release
                                só funcionam a partir do próximo build da app — até lá usa o link web.
                            </p>
                        </div>
                    </div>
                </div>

                {/* ------------------------- right column ------------------------ */}
                <div style={s.col}>
                    <div style={s.card}>
                        <h3 style={s.cardTitle}>3. Segmentação</h3>
                        <p style={s.cardDesc}>Deixa em "Todos" para não filtrar por esse critério.</p>

                        <label style={s.fieldLabel}>Actividade</label>
                        <select value={activity} onChange={(e) => setActivity(e.target.value)} style={{ ...s.input, width: '100%', marginBottom: 12 }}>
                            {ACTIVITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>

                        <label style={s.fieldLabel}>Tipo de conta</label>
                        <select value={accType} onChange={(e) => setAccType(e.target.value)} style={{ ...s.input, width: '100%', marginBottom: 12 }}>
                            {ACC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>

                        <label style={s.fieldLabel}>Subscrição</label>
                        <select value={premium} onChange={(e) => setPremium(e.target.value)} style={{ ...s.input, width: '100%', marginBottom: 12 }}>
                            {PREMIUM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>

                        <label style={s.fieldLabel}>País</label>
                        <select value={country} onChange={(e) => setCountry(e.target.value)} style={{ ...s.input, width: '100%' }}>
                            <option value="">Todos</option>
                            {countries.map((c) => (
                                <option key={c.country} value={c.country}>{c.country} ({c.users})</option>
                            ))}
                        </select>

                        <div style={s.audienceBox}>
                            <div style={s.audienceLabel}>Destinatários</div>
                            <div style={s.audienceNum}>
                                {countingAudience ? '...' : (audienceCount === null ? '—' : audienceCount.toLocaleString('pt-PT'))}
                            </div>
                            <p style={s.audienceNote}>
                                Nem todos têm push activo. Actualmente cerca de 449 dispositivos estão
                                subscritos, portanto o número entregue será menor.
                            </p>
                        </div>
                    </div>

                    <div style={{ ...s.card, marginTop: 16 }}>
                        <h3 style={s.cardTitle}>4. Enviar</h3>

                        <label style={s.fieldLabel}>Conta de teste (email ou user_id da app)</label>
                        <input
                            type="text"
                            value={testTarget}
                            onChange={(e) => setTestTarget(e.target.value)}
                            placeholder="o.teu.email@exemplo.com"
                            style={{ ...s.input, width: '100%', marginBottom: 6 }}
                        />
                        <p style={{ margin: '0 0 14px', fontSize: 11, color: '#999' }}>
                            Tem de ser uma conta da app (não a conta de admin do painel). Envia sempre um
                            teste antes do envio geral.
                        </p>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button onClick={sendTest} disabled={sending} style={sending ? s.btnDisabled : s.btnSecondary}>
                                Enviar teste
                            </button>
                            <button onClick={sendBroadcast} disabled={sending} style={sending ? s.btnDisabled : s.btnPrimary}>
                                {sending ? 'A enviar...' : `Enviar a ${audienceCount ?? 0} utilizador(es)`}
                            </button>
                        </div>

                        {feedback && (
                            <div style={feedback.type === 'ok' ? s.alertOk : s.alertError}>{feedback.text}</div>
                        )}
                    </div>
                </div>
            </div>

            {/* -------------------------- history -------------------------- */}
            <div style={{ ...s.card, marginTop: 20 }}>
                <h3 style={s.cardTitle}>Histórico de envios</h3>
                <p style={s.cardDesc}>Entregues e aberturas vêm directamente do OneSignal.</p>

                {historyLoading ? (
                    <p style={s.empty}>A carregar...</p>
                ) : history.length === 0 ? (
                    <p style={s.empty}>Ainda não foi enviada nenhuma notificação.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Data</th>
                                    <th style={s.th}>Título</th>
                                    <th style={s.th}>Segmento</th>
                                    <th style={s.th}>Alvo</th>
                                    <th style={s.th}>Entregues</th>
                                    <th style={s.th}>Aberturas</th>
                                    <th style={s.th}>CTR</th>
                                    <th style={s.th}>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((c) => (
                                    <tr key={c.id} style={s.tr}>
                                        <td style={s.td}>{new Date(c.created_at).toLocaleString('pt-PT')}</td>
                                        <td style={s.td}>
                                            {c.title}
                                            {Number(c.is_test) === 1 && <span style={s.testBadge}>teste</span>}
                                            {String(c.content_type || '').startsWith('daily_') && <span style={s.autoBadge}>automático</span>}
                                        </td>
                                        <td style={{ ...s.td, fontSize: 11, color: '#999', maxWidth: 220 }}>
                                            {describeFilters(c.filters_json)}
                                        </td>
                                        <td style={s.td}>{Number(c.recipients).toLocaleString('pt-PT')}</td>
                                        <td style={s.td}>{c.delivered != null ? c.delivered.toLocaleString('pt-PT') : '—'}</td>
                                        <td style={s.td}>{c.clicked != null ? c.clicked.toLocaleString('pt-PT') : '—'}</td>
                                        <td style={s.td}>{c.ctr != null ? `${c.ctr}%` : '—'}</td>
                                        <td style={s.td}>
                                            <span style={statusStyle(c.status)}>{c.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function describeFilters(json) {
    try {
        const f = JSON.parse(json || '{}');
        const parts = Object.entries(f)
            .filter(([, v]) => v !== '' && v != null)
            .map(([k, v]) => `${k}: ${v}`);
        return parts.length ? parts.join(', ') : 'Todos';
    } catch {
        return '—';
    }
}

function statusStyle(status) {
    const colors = { sent: '#10b981', partial: '#f59e0b', failed: '#ef4444' };
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        color: '#fff',
        backgroundColor: colors[status] || '#9ca3af',
    };
}

const s = {
    container: { padding: '0 20px 40px 20px', maxWidth: 1400 },
    header: { marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
    subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999', fontWeight: 400, maxWidth: 700 },
    columns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, alignItems: 'start' },
    col: { minWidth: 0 },
    card: { padding: 24, backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
    cardTitle: { fontSize: 15, fontWeight: 600, color: '#333', margin: '0 0 4px' },
    cardDesc: { margin: '0 0 14px', fontSize: 12, color: '#aaa' },
    fieldLabel: { display: 'block', fontSize: 11, color: '#888', fontWeight: 500, marginBottom: 4 },
    counter: { fontSize: 10, color: '#c4c4c4', textAlign: 'right', marginBottom: 10 },
    input: { padding: '8px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#1f2937', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
    chipRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
    chip: { padding: '6px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
    chipActive: { padding: '6px 12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    pickerList: { maxHeight: 340, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 },
    pickerRow: { display: 'flex', alignItems: 'center', gap: 10, padding: 8, cursor: 'pointer', borderBottom: '1px solid #f7f7f7' },
    selectedBox: { display: 'flex', alignItems: 'center', gap: 10, padding: 10, marginBottom: 10, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8 },
    thumb: { width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 },
    thumbPlaceholder: { width: 40, height: 40, borderRadius: 6, background: '#eee', flexShrink: 0 },
    itemTitle: { fontSize: 13, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    itemSub: { fontSize: 11, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    itemMeta: { fontSize: 10, color: '#bbb', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10 },
    pagerBtn: { padding: '5px 10px', background: '#fff', color: '#4338ca', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 },
    pagerBtnDisabled: { padding: '5px 10px', background: '#f9fafb', color: '#d1d5db', border: '1px solid #f0f0f0', borderRadius: 6, cursor: 'not-allowed', fontSize: 11, fontWeight: 600 },
    pagerLabel: { fontSize: 11, color: '#999' },
    pickerNote: { margin: '8px 0 0', fontSize: 10, color: '#bbb' },
    removeBtn: { padding: '4px 10px', background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 500 },
    helpBox: { marginTop: 14, padding: 12, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, fontSize: 12 },
    helpGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    code: { padding: '3px 7px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11, color: '#6366f1' },
    audienceBox: { marginTop: 18, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, textAlign: 'center' },
    audienceLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', fontWeight: 600 },
    audienceNum: { fontSize: 32, fontWeight: 700, color: '#6366f1', margin: '4px 0' },
    audienceNote: { margin: 0, fontSize: 11, color: '#aaa' },
    btnPrimary: { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    btnSecondary: { padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    btnDisabled: { padding: '10px 18px', background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 8, cursor: 'not-allowed', fontSize: 13, fontWeight: 500 },
    alertOk: { marginTop: 14, padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#065f46', fontSize: 13 },
    alertError: { marginTop: 14, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 },
    empty: { color: '#ccc', textAlign: 'center', padding: 30, fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f5f5f5' },
    td: { padding: '10px 8px', fontSize: 13, color: '#374151' },
    testBadge: { marginLeft: 6, padding: '1px 6px', background: '#e0e7ff', color: '#4338ca', borderRadius: 8, fontSize: 10, fontWeight: 600 },
    autoBadge: { marginLeft: 6, padding: '1px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 8, fontSize: 10, fontWeight: 600 },
};
